import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { toast } from 'react-toastify';
import { fetchBookings, addPayment, fetchBookingById, updateBooking } from '../api/Booking';
import { fetchCustomerById } from '../api/Customer';
import { updateQueryStatus } from '../api/Query';
import { fetchUsers } from '../api/User';
import Layout from '../components/Layout';
import CustomerInfoModal from '../components/Customer/CustomerInfoModal';
import AddBookingModal from '../components/Booking/AddBookingModal';
import AddPaymentModal from '../components/Booking/AddPaymentModal';
import AssignmentModal from '../components/Booking/AssignmentModal';
import BookingComponentTables from '../components/Booking/BookingComponentTables';
import PaymentHistoryModal from '../components/Booking/PaymentHistoryModal';
import QueryInfoModal from '../components/Query/QueryInfoModal';
import QueryPickerModal from '../components/Query/QueryPickerModal';
import {
  buildBookingComponentsSummary,
  buildBookingComponentsSummaryFromChildren,
  BookingComponentsSummary,
  getBookingComponentType,
} from '../utils/bookingComponents';
import { getApiHeaders } from '../utils/apiHeaders';
import { formatDate, parseDateValue } from '../utils/date';
import { Booking } from '../types/Booking';
import { Payment } from '../types/Payment';
import { Query } from '../types/Query';
import { User } from '../types/User';
import '../css/Pages.css';
import '../css/PackagesPage.css';
import '../css/BookingsPage.css';
import * as FiIcons from 'react-icons/fi';

const FiAlertCircle = FiIcons.FiAlertCircle as unknown as React.FC;
const FiArrowRight = FiIcons.FiArrowRight as unknown as React.FC;
const FiBriefcase = FiIcons.FiBriefcase as unknown as React.FC;
const FiCalendar = FiIcons.FiCalendar as unknown as React.FC;
const FiCheckCircle = FiIcons.FiCheckCircle as unknown as React.FC;
const FiChevronDown = FiIcons.FiChevronDown as unknown as React.FC<{ className?: string }>;
const FiCreditCard = FiIcons.FiCreditCard as unknown as React.FC;
const FiEye = FiIcons.FiEye as unknown as React.FC;
const FiFileText = FiIcons.FiFileText as unknown as React.FC;
const FiGrid = FiIcons.FiGrid as unknown as React.FC;
const FiHome = FiIcons.FiHome as unknown as React.FC;
const FiList = FiIcons.FiList as unknown as React.FC;
const FiMapPin = FiIcons.FiMapPin as unknown as React.FC;
const FiPlus = FiIcons.FiPlus as unknown as React.FC;
const FiRefreshCw = FiIcons.FiRefreshCw as unknown as React.FC;
const FiSearch = FiIcons.FiSearch as unknown as React.FC;
const FiSend = FiIcons.FiSend as unknown as React.FC;
const FiSettings = FiIcons.FiSettings as unknown as React.FC;
const FiTruck = FiIcons.FiTruck as unknown as React.FC;
const FiUpload = FiIcons.FiUpload as unknown as React.FC;
const FiUser = FiIcons.FiUser as unknown as React.FC;
const FiUserPlus = FiIcons.FiUserPlus as unknown as React.FC;

const toText = (value: unknown, fallback = '') => {
  if (value === null || value === undefined) return fallback;
  return String(value).trim() || fallback;
};

const formatMoney = (amount: number) => `₹${Number(amount || 0).toLocaleString('en-IN')}`;

const getStartOfToday = () => {
  const date = new Date();
  date.setHours(0, 0, 0, 0);
  return date;
};

const daysBetween = (start?: string) => {
  const parsed = parseDateValue(start);
  if (!parsed || Number.isNaN(parsed.getTime())) return Number.POSITIVE_INFINITY;
  const today = getStartOfToday();
  parsed.setHours(0, 0, 0, 0);
  return Math.ceil((parsed.getTime() - today.getTime()) / (1000 * 60 * 60 * 24));
};

const formatTravelDateRange = (start?: string, end?: string) => {
  const startDate = parseDateValue(start);
  const endDate = parseDateValue(end);
  if (!startDate || !endDate || Number.isNaN(startDate.getTime()) || Number.isNaN(endDate.getTime())) {
    return 'Travel dates pending';
  }

  return `${startDate.toLocaleDateString('en-GB', { day: 'numeric', month: 'short', year: 'numeric' })} - ${endDate.toLocaleDateString('en-GB', { day: 'numeric', month: 'short', year: 'numeric' })}`;
};

const getNightCount = (start?: string, end?: string) => {
  const startDate = parseDateValue(start);
  const endDate = parseDateValue(end);
  if (!startDate || !endDate || Number.isNaN(startDate.getTime()) || Number.isNaN(endDate.getTime())) return 0;
  return Math.max(0, Math.round((endDate.getTime() - startDate.getTime()) / (1000 * 60 * 60 * 24)));
};

const getTravellerLabel = (booking: Booking) => {
  const adults = Number((booking as any).num_adults || booking.query?.pax || 0);
  const children = Number((booking as any).num_children || 0);
  const infants = Number((booking as any).num_infants || 0);
  const labels = [
    adults > 0 ? `${adults} Adult${adults === 1 ? '' : 's'}` : '',
    children > 0 ? `${children} Child${children === 1 ? '' : 'ren'}` : '',
    infants > 0 ? `${infants} Infant${infants === 1 ? '' : 's'}` : '',
  ].filter(Boolean);

  return labels.length > 0 ? labels.join(' • ') : 'Traveller count pending';
};

const getBookingReference = (booking: Booking) => {
  return toText(
    (booking as any).booking_reference
      || (booking as any).reference_code
      || (booking as any).booking_code
      || (booking as any).reference
      || '',
    `#${String(booking.id)}`,
  );
};

const getStatusTone = (status: string) => {
  const normalized = status.toLowerCase();
  if (normalized.includes('confirm') || normalized.includes('complete')) {
    return { className: 'is-success', label: status || 'Confirmed' };
  }
  if (normalized.includes('part')) {
    return { className: 'is-info', label: status };
  }
  if (normalized.includes('cancel') || normalized.includes('failed')) {
    return { className: 'is-danger', label: status };
  }
  if (normalized.includes('book') || normalized.includes('pending')) {
    return { className: 'is-warning', label: status };
  }
  return { className: 'is-neutral', label: status || 'Status pending' };
};

const getPaymentStatus = (paid: number, total: number, travelStartDate?: string) => {
  const balance = Math.max(0, total - paid);
  const daysToTravel = daysBetween(travelStartDate);
  const isOverdue = balance > 0 && daysToTravel <= 7;

  if (total > 0 && balance <= 0) return 'paid';
  if (isOverdue) return 'overdue';
  if (paid > 0) return 'partial';
  return 'pending';
};

const getComponentVoucherMap = (booking: Booking) => {
  const extras = (booking as any)?.extras;
  if (!extras || typeof extras !== 'object') return {};
  const componentVouchers = (extras as any).component_vouchers || (extras as any).componentVouchers;
  return componentVouchers && typeof componentVouchers === 'object' ? componentVouchers : {};
};

const getVoucherStatus = (booking: Booking, children: any[]) => {
  const parentHasVoucher = Boolean((booking as any).pdf_url || (booking as any).voucher_url);
  const componentVoucherMap = getComponentVoucherMap(booking);
  const componentVoucherCount = Object.values(componentVoucherMap).filter(Boolean).length;
  const planSummary = buildBookingComponentsSummary({
    bookingPlan: (booking as any).booking_plan || (booking as any).bookingPlan,
    travelStartDate: booking.travel_start_date,
    travelEndDate: booking.travel_end_date,
  });
  const planComponentCount = [
    ...(planSummary?.hotels || []),
    ...(planSummary?.flights || []),
    ...(planSummary?.transfers || []),
    ...(planSummary?.activities || []),
    ...(planSummary?.visas || []),
    ...(planSummary?.insurance || []),
  ].length;
  const childCount = children.length;
  const uploadedChildren = children.filter((child) => child?.pdf_url || child?.voucher_url).length;

  if (parentHasVoucher) return 'uploaded';
  if (planComponentCount > 0 && componentVoucherCount >= planComponentCount) return 'uploaded';
  if (componentVoucherCount > 0) return 'partial';
  if (childCount > 0 && uploadedChildren > 0) return 'partial';
  return 'pending';
};

const getAssignedName = (booking: Booking, allUsers: User[]) => {
  if (booking.user?.name) return booking.user.name;
  const assignedUser = allUsers.find((user: any) => String(user.id) === String((booking as any).assigned_to));
  return assignedUser?.name || '';
};

const getActivityCount = (children: any[]) => children.filter((child) => getBookingComponentType(child) === 'activity').length;

const buildPagination = (page: number, totalPages: number) => {
  if (totalPages <= 1) return [1];

  const pages = new Set<number>();
  pages.add(1);
  pages.add(totalPages);

  for (let current = Math.max(1, page - 1); current <= Math.min(totalPages, page + 1); current += 1) {
    pages.add(current);
  }

  const sorted = Array.from(pages).sort((left, right) => left - right);
  return sorted.reduce<Array<number | 'ellipsis'>>((acc, value, index) => {
    const previous = sorted[index - 1];
    if (index > 0 && previous !== undefined && value - previous > 1) acc.push('ellipsis');
    acc.push(value);
    return acc;
  }, []);
};

type BookingCard = {
  booking: Booking;
  assignedName: string;
  children: any[];
  componentSummary: ReturnType<typeof buildBookingComponentsSummary>;
  paymentStatus: string;
  paymentPercentage: number;
  paidAmount: number;
  totalAmount: number;
  balanceAmount: number;
  voucherStatus: string;
  componentCounts: {
    hotels: number;
    cabs: number;
    flights: number;
    activities: number;
  };
  dateRange: string;
  nights: number;
  travellerLabel: string;
  title: string;
  statusTone: { className: string; label: string };
  travelStartTimestamp: number;
  isUpcoming: boolean;
  isUpcomingInSevenDays: boolean;
  isCompleted: boolean;
  searchableText: string;
};

const BookingsPage = () => {
  const hasFetched = useRef(false);

  const [bookings, setBookings] = useState<Booking[]>([]);
  const [allUsers, setAllUsers] = useState<User[]>([]);
  const [statusFilter, setStatusFilter] = useState('');
  const [searchTerm, setSearchTerm] = useState('');
  const [paymentFilter, setPaymentFilter] = useState('');
  const [travelDateFilter, setTravelDateFilter] = useState('');
  const [assignedFilter, setAssignedFilter] = useState('');
  const [voucherFilter, setVoucherFilter] = useState('');
  const [quickFilter, setQuickFilter] = useState('');
  const [sortBy, setSortBy] = useState('travel-newest');
  const [layout, setLayout] = useState<'list' | 'compact'>('list');
  const [currentPage, setCurrentPage] = useState(1);
  const [pageSize, setPageSize] = useState(25);
  const [expandedBookingId, setExpandedBookingId] = useState<number | string | null>(null);
  const [selectedCustomer, setSelectedCustomer] = useState<any>(null);
  const [selectedQueryId, setSelectedQueryId] = useState<number | null>(null);
  const [showAssignmentModal, setShowAssignmentModal] = useState(false);
  const [showPaymentHistory, setShowPaymentHistory] = useState(false);
  const [showAddPaymentModal, setShowAddPaymentModal] = useState(false);
  const [showQueryPicker, setShowQueryPicker] = useState(false);
  const [showCreateBookingModal, setShowCreateBookingModal] = useState(false);
  const [selectedQueryForBooking, setSelectedQueryForBooking] = useState<Query | null>(null);
  const [selectedBooking, setSelectedBooking] = useState<Booking | null>(null);
  const [selectedBookingWithHistory, setSelectedBookingWithHistory] = useState<Booking | null>(null);
  const [loadingHistory, setLoadingHistory] = useState(false);
  const [uploadingComponents, setUploadingComponents] = useState<Record<string, boolean>>({});
  const componentFileInputsRef = useRef<Record<string, HTMLInputElement | null>>({});

  const loadBookings = useCallback(async () => {
    const data = await fetchBookings();
    const bookingsList = Array.isArray(data) ? data : [];
    setBookings(bookingsList);
  }, []);

  const loadUsers = useCallback(async () => {
    try {
      const users = await fetchUsers();
      setAllUsers(Array.isArray(users) ? users : []);
    } catch (err) {
      console.error('Failed to load users:', err);
    }
  }, []);

  useEffect(() => {
    if (!hasFetched.current) {
      hasFetched.current = true;
      void loadBookings();
      void loadUsers();
    }
  }, [loadBookings, loadUsers]);

  const getParentId = (booking: any): string | number | null => (
    booking.parent_id || booking.parentId || booking.parent_booking_id || booking.parentBookingId || null
  );

  const getPaidAmount = useCallback((booking: Booking): number => {
    if ((booking as any).payment_details?.payment_amount !== undefined) {
      return Number((booking as any).payment_details.payment_amount) || 0;
    }
    return (booking.payments || []).reduce((sum: number, payment: Payment) => sum + (Number(payment.amount) || 0), 0);
  }, []);

  const childrenByParentId = useMemo(() => {
    const map = new Map<string | number, any[]>();
    (bookings as any[]).forEach((booking: any) => {
      const parentId = getParentId(booking);
      if (parentId) {
        if (!map.has(parentId)) map.set(parentId, []);
        map.get(parentId)!.push(booking);
      }
    });
    return map;
  }, [bookings]);

  const parentBookings = useMemo(
    () => (bookings as any[]).filter((booking: any) => !getParentId(booking)),
    [bookings],
  );

  const bookingCards = useMemo<BookingCard[]>(() => (
    parentBookings.map((booking) => {
      const bookingId = typeof booking.id === 'string' ? booking.id : booking.id;
      const children = childrenByParentId.get(bookingId) || [];
      const planSummary = buildBookingComponentsSummary({
        bookingPlan: (booking as any).booking_plan || (booking as any).bookingPlan,
        travelStartDate: booking.travel_start_date,
        travelEndDate: booking.travel_end_date,
        totalAmount: booking.total_price,
      });
      const childSummary = buildBookingComponentsSummaryFromChildren(children);
      const componentSummary = planSummary ? {
        ...planSummary,
        hotels: planSummary.hotels.length ? planSummary.hotels : (childSummary?.hotels || []),
        flights: planSummary.flights.length ? planSummary.flights : (childSummary?.flights || []),
        transfers: planSummary.transfers.length ? planSummary.transfers : (childSummary?.transfers || []),
        cabLine: planSummary.cabLine || childSummary?.cabLine || '',
      } : childSummary;
      const paidAmount = getPaidAmount(booking);
      const totalAmount = Number(booking.total_price || 0);
      const paymentPercentage = totalAmount > 0 ? Math.min(100, Math.round((paidAmount / totalAmount) * 100)) : 0;
      const balanceAmount = Math.max(0, totalAmount - paidAmount);
      const paymentStatus = getPaymentStatus(paidAmount, totalAmount, booking.travel_start_date);
      const voucherStatus = getVoucherStatus(booking, children);
      const assignedName = getAssignedName(booking, allUsers);
      const nights = getNightCount(booking.travel_start_date, booking.travel_end_date);
      const travelStartDate = parseDateValue(booking.travel_start_date);
      const daysToTravel = daysBetween(booking.travel_start_date);
      const travelStartTimestamp = travelStartDate && !Number.isNaN(travelStartDate.getTime())
        ? travelStartDate.getTime()
        : 0;
      const isUpcoming = Number.isFinite(daysToTravel) && daysToTravel >= 0;
      const isUpcomingInSevenDays = Number.isFinite(daysToTravel) && daysToTravel >= 0 && daysToTravel <= 7;

      const componentValues = [
        ...(componentSummary?.hotels || []),
        ...(componentSummary?.flights || []),
        ...(componentSummary?.transfers || []),
        ...(componentSummary?.activities || []),
        ...(componentSummary?.visas || []),
        ...(componentSummary?.insurance || []),
      ].flatMap((item: any) => Object.values(item || {}));

      const statusTone = getStatusTone(toText(booking.status, 'Booking pending'));
      const isCompleted = statusTone.className === 'is-success' && paymentStatus === 'paid';

      return {
        booking,
        assignedName,
        children,
        componentSummary,
        paymentStatus,
        paymentPercentage,
        paidAmount,
        totalAmount,
        balanceAmount,
        voucherStatus,
        componentCounts: {
          hotels: componentSummary?.hotels.length || 0,
          cabs: componentSummary?.transfers.length || (componentSummary?.cabLine ? 1 : 0),
          flights: componentSummary?.flights.length || 0,
          activities: componentSummary?.activities.length || getActivityCount(children),
        },
        dateRange: formatTravelDateRange(booking.travel_start_date, booking.travel_end_date),
        nights,
        travellerLabel: getTravellerLabel(booking),
        title: booking.package?.title || booking.query?.location || 'Untitled booking',
        statusTone,
        travelStartTimestamp,
        isUpcoming,
        isUpcomingInSevenDays,
        isCompleted,
        searchableText: [
          booking.package?.title || '',
          booking.customer?.name || '',
          String(booking.id),
          String(booking.query_id || ''),
          assignedName,
          ...componentValues.map((value) => toText(value)),
        ].join(' ').toLowerCase(),
      };
    })
  ), [allUsers, childrenByParentId, getPaidAmount, parentBookings]);

  const statusOptions = useMemo(() => {
    const unique = new Set<string>();
    bookingCards.forEach(({ booking }) => {
      const status = toText(booking.status);
      if (status) unique.add(status);
    });
    return Array.from(unique);
  }, [bookingCards]);

  const assignedUserOptions = useMemo(() => {
    const seen = new Set<string>();
    return allUsers.filter((user: any) => {
      if (!user?.id || seen.has(String(user.id))) return false;
      seen.add(String(user.id));
      return true;
    });
  }, [allUsers]);

  const filteredBookings = useMemo(() => {
    const query = searchTerm.trim().toLowerCase();
    const filtered = bookingCards.filter((card) => {
      const matchesSearch = !query || card.searchableText.includes(query);
      if (!matchesSearch) return false;

      const matchesStatus = !statusFilter || toText(card.booking.status).toLowerCase() === statusFilter.toLowerCase();
      if (!matchesStatus) return false;

      const matchesPayment = !paymentFilter || card.paymentStatus === paymentFilter;
      if (!matchesPayment) return false;

      const matchesTravelDate = (() => {
        if (!travelDateFilter) return true;
        if (travelDateFilter === 'upcoming') return card.isUpcoming;
        if (travelDateFilter === 'upcoming-7') return card.isUpcomingInSevenDays;
        if (travelDateFilter === 'past') return !card.isUpcoming && Number.isFinite(daysBetween(card.booking.travel_start_date));
        return true;
      })();
      if (!matchesTravelDate) return false;

      const matchesAssigned = (() => {
        if (!assignedFilter) return true;
        if (assignedFilter === 'assigned') return Boolean(card.assignedName);
        if (assignedFilter === 'unassigned') return !card.assignedName;
        return String((card.booking as any).assigned_to || card.booking.user?.id || '') === assignedFilter;
      })();
      if (!matchesAssigned) return false;

      const matchesVoucher = !voucherFilter || card.voucherStatus === voucherFilter;
      if (!matchesVoucher) return false;

      const matchesQuickFilter = (() => {
        if (!quickFilter) return true;
        if (quickFilter === 'upcoming') return card.isUpcoming;
        if (quickFilter === 'payment-overdue') return card.paymentStatus === 'overdue';
        if (quickFilter === 'voucher-pending') return card.voucherStatus !== 'uploaded';
        if (quickFilter === 'unassigned') return !card.assignedName;
        if (quickFilter === 'completed') return card.isCompleted;
        return true;
      })();

      return matchesQuickFilter;
    });

    return filtered.sort((left, right) => {
      if (sortBy === 'travel-oldest') return left.travelStartTimestamp - right.travelStartTimestamp;
      if (sortBy === 'payment-highest') return right.balanceAmount - left.balanceAmount;
      if (sortBy === 'payment-lowest') return left.balanceAmount - right.balanceAmount;
      if (sortBy === 'created-newest') {
        return new Date(right.booking.created_at).getTime() - new Date(left.booking.created_at).getTime();
      }
      return right.travelStartTimestamp - left.travelStartTimestamp;
    });
  }, [assignedFilter, bookingCards, paymentFilter, quickFilter, searchTerm, sortBy, statusFilter, travelDateFilter, voucherFilter]);

  useEffect(() => {
    setCurrentPage(1);
  }, [assignedFilter, layout, pageSize, paymentFilter, quickFilter, searchTerm, sortBy, statusFilter, travelDateFilter, voucherFilter]);

  useEffect(() => {
    const totalPages = Math.max(1, Math.ceil(filteredBookings.length / pageSize));
    if (currentPage > totalPages) {
      setCurrentPage(totalPages);
    }
  }, [currentPage, filteredBookings.length, pageSize]);

  const totalPages = Math.max(1, Math.ceil(filteredBookings.length / pageSize));
  const paginationItems = buildPagination(currentPage, totalPages);
  const visibleBookings = useMemo(() => {
    const startIndex = (currentPage - 1) * pageSize;
    return filteredBookings.slice(startIndex, startIndex + pageSize);
  }, [currentPage, filteredBookings, pageSize]);

  const summaryCounts = useMemo(() => ({
    total: bookingCards.length,
    paymentPending: bookingCards.filter((card) => card.balanceAmount > 0).length,
    upcomingSevenDays: bookingCards.filter((card) => card.isUpcomingInSevenDays).length,
    voucherPending: bookingCards.filter((card) => card.voucherStatus !== 'uploaded').length,
  }), [bookingCards]);

  const openAssignmentModal = (booking: Booking) => {
    setSelectedBooking(booking);
    setShowAssignmentModal(true);
  };

  const handleAssign = async (bookingId: number | string, userId: number) => {
    setBookings((previous) =>
      previous.map((booking) =>
        booking.id === bookingId ? { ...booking, assigned_to: userId } : booking,
      ),
    );
  };

  const openPaymentHistoryModal = async (booking: Booking) => {
    setSelectedBooking(booking);
    setShowPaymentHistory(true);
    setLoadingHistory(true);
    try {
      const bookingDetails = await fetchBookingById(booking.id);
      const paymentHistory = (bookingDetails.payment_history || []).map((payment: any) => ({
        id: payment.id,
        amount: Number(payment.amount_paise || 0) / 100,
        method: payment.method || payment.provider || '',
        status: payment.status || '',
        created_at: payment.created_at,
        transactionId: payment.notes?.transactionId || null,
        transaction_id: payment.notes?.transactionId || null,
        notes: payment.notes?.notes || null,
      }));
      const bookingAmount = bookingDetails.payment_details?.booking_amount || 0;
      const totalPrice = bookingAmount > 100000 ? bookingAmount / 100 : bookingAmount;

      const bookingWithHistory: Booking = {
        ...booking,
        payments: paymentHistory,
        total_price: totalPrice || bookingDetails.total_price || booking.total_price,
      };
      setSelectedBookingWithHistory(bookingWithHistory);
    } catch (error: any) {
      toast.error(error?.message || 'Failed to load payment history');
      setSelectedBookingWithHistory(booking);
    } finally {
      setLoadingHistory(false);
    }
  };

  const openAddPaymentModal = (booking: Booking) => {
    setSelectedBooking(booking);
    setShowAddPaymentModal(true);
  };

  const handleAddPayment = async (paymentData: {
    amount: number;
    method: string;
    transactionId: string;
    notes?: string;
  }) => {
    if (!selectedBooking) return;
    try {
      const paymentDataInPaisa = {
        ...paymentData,
        amount: Math.round(paymentData.amount * 100),
      };
      await addPayment(selectedBooking.id, paymentDataInPaisa);
      toast.success(`Payment of ₹${paymentData.amount.toLocaleString('en-IN')} captured successfully!`);
      await loadBookings();
    } catch (error: any) {
      toast.error(error?.message || 'Error adding payment.');
    }
  };

  const handleBookingCreated = async (query: Query) => {
    try {
      await updateQueryStatus(query.id, 'BOOKED');
      toast.success('Booking created');
    } catch (error: any) {
      toast.error(error?.message || 'Failed to update query status');
    } finally {
      void loadBookings();
    }
  };

  const checkAndUpdateParentBookingStatus = async (parentBookingId: string | number) => {
    try {
      const updatedBookings = await fetchBookings();
      const updatedChildrenMap = new Map<string | number, any[]>();
      updatedBookings.forEach((booking: any) => {
        const parentId = getParentId(booking);
        if (parentId) {
          if (!updatedChildrenMap.has(parentId)) updatedChildrenMap.set(parentId, []);
          updatedChildrenMap.get(parentId)!.push(booking);
        }
      });

      const children = updatedChildrenMap.get(parentBookingId) || [];
      const parentBooking = updatedBookings.find((booking: any) => booking.id === parentBookingId);
      if (!parentBooking || children.length === 0) return;

      const allComponentsBooked = children.every((child: any) => {
        const hasVoucher = child.voucher_url || child.pdf_url;
        if (hasVoucher) return true;

        const status = String(child.status || child.booking_status || child.state || '').toLowerCase();
        return status.includes('book') || status.includes('confirm');
      });

      const parentStatus = String(parentBooking.status || '').toUpperCase();
      if (allComponentsBooked && !['BOOKING', 'BOOKED', 'CONFIRMED'].includes(parentStatus)) {
        await updateBooking(parentBookingId, { status: 'BOOKING' });
        toast.success('All components booked! Parent booking marked as booking.');
        await loadBookings();
      }
    } catch (error: any) {
      console.error('Failed to update parent booking status:', error);
    }
  };

  const openCustomerModal = async (customerId?: number) => {
    if (!customerId) return;
    try {
      const customer = await fetchCustomerById(customerId);
      setSelectedCustomer(customer);
    } catch (error) {
    }
  };

  const resetFilters = () => {
    setSearchTerm('');
    setStatusFilter('');
    setPaymentFilter('');
    setTravelDateFilter('');
    setAssignedFilter('');
    setVoucherFilter('');
    setQuickFilter('');
    setSortBy('travel-newest');
    setCurrentPage(1);
    setExpandedBookingId(null);
    void loadBookings();
  };

  const toggleBookingExpansion = (bookingId: number | string) => {
    setExpandedBookingId((current) => (current === bookingId ? null : bookingId));
  };

  const stopCardToggle = (event: React.SyntheticEvent) => {
    event.stopPropagation();
  };

  const handleVoucherUploaded = async (parentBookingId: string | number) => {
    await loadBookings();
    setTimeout(async () => {
      await checkAndUpdateParentBookingStatus(parentBookingId);
    }, 300);
  };

  const handleComponentVoucherUpload = async (
    booking: Booking,
    componentId: string,
    file: File | null,
  ) => {
    if (!file) return;

    const uploadKey = `${String(booking.id)}:${componentId}`;
    setUploadingComponents((current) => ({ ...current, [uploadKey]: true }));

    try {
      const formData = new FormData();
      formData.append('voucher', file);

      const API_BASE_URL = process.env.REACT_APP_TRAVEL_API_BASE_URL;
      const res = await fetch(`${API_BASE_URL}/bookings/${booking.id}/voucher`, {
        method: 'POST',
        headers: getApiHeaders('form-data'),
        body: formData,
      });

      let responsePayload: any = null;
      try {
        responsePayload = await res.clone().json();
      } catch {
        responsePayload = null;
      }

      if (!res.ok) {
        const errorText = await res.text();
        throw new Error(errorText || 'Failed to upload voucher');
      }

      const refreshedBookings = await fetchBookings();
      const refreshedBooking = (refreshedBookings as any[]).find((item) => String(item.id) === String(booking.id)) as Booking | undefined;
      const uploadedUrl = responsePayload?.voucher_url
        || responsePayload?.pdf_url
        || responsePayload?.url
        || (refreshedBooking as any)?.voucher_url
        || (refreshedBooking as any)?.pdf_url
        || '';

      if (uploadedUrl) {
        const baseBooking = refreshedBooking || booking;
        const currentVouchers = getComponentVoucherMap(baseBooking);
        const nextExtras = {
          ...(((baseBooking as any)?.extras && typeof (baseBooking as any).extras === 'object') ? (baseBooking as any).extras : {}),
          component_vouchers: {
            ...currentVouchers,
            [componentId]: uploadedUrl,
          },
        };
        await updateBooking(booking.id, { extras: nextExtras } as any);
      }

      toast.success('Voucher uploaded successfully!');
      await handleVoucherUploaded(booking.id);
    } catch (error: any) {
      toast.error(error?.message || 'Failed to upload voucher');
    } finally {
      setUploadingComponents((current) => ({ ...current, [uploadKey]: false }));
    }
  };

  const renderComponentVoucherActions = (
    booking: Booking,
    row: {
      type: 'hotel' | 'flight' | 'transfer' | 'activity' | 'visa' | 'insurance';
      item: BookingComponentsSummary['hotels'][number]
        | BookingComponentsSummary['flights'][number]
        | BookingComponentsSummary['transfers'][number]
        | BookingComponentsSummary['activities'][number]
        | BookingComponentsSummary['visas'][number]
        | BookingComponentsSummary['insurance'][number];
      index: number;
    },
  ) => {
    const fallbackComponentId = `${row.type}-${row.index + 1}`;
    const componentId = String((row.item as any).componentId || fallbackComponentId);
    const uploadKey = `${String(booking.id)}:${componentId}`;
    const voucherMap = getComponentVoucherMap(booking);
    const voucherUrl = String(voucherMap[componentId] || (row.item as any).voucherUrl || '');
    const isUploading = Boolean(uploadingComponents[uploadKey]);
    const displayName = String(
      (row.item as any).name
        || (row.item as any).title
        || (row.item as any).flightNumber
        || (row.item as any).vehicle
        || `${row.type} ${row.index + 1}`,
    );

    return (
      <div className="booking-component-table__actions">
        <input
          ref={(node) => {
            componentFileInputsRef.current[uploadKey] = node;
          }}
          type="file"
          accept=".pdf,.jpg,.jpeg,.png"
          style={{ display: 'none' }}
          disabled={isUploading}
          onChange={(event) => {
            const file = event.target.files?.[0] || null;
            if (file) {
              void handleComponentVoucherUpload(booking, componentId, file);
            }
            event.target.value = '';
          }}
        />

        {voucherUrl ? (
          <a
            href={voucherUrl}
            target="_blank"
            rel="noopener noreferrer"
            className="booking-component-table__button booking-component-table__button--view"
            aria-label={`View Voucher for ${displayName}`}
          >
            <FiEye /> View Voucher
          </a>
        ) : (
          <button
            type="button"
            className="booking-component-table__button booking-component-table__button--disabled"
            aria-label={`View Voucher for ${displayName}`}
            disabled
          >
            <FiEye /> View Voucher
          </button>
        )}

        <button
          type="button"
          className={`booking-component-table__button booking-component-table__button--upload${isUploading ? ' is-disabled' : ''}`}
          aria-label={`Upload Voucher for ${displayName}`}
          disabled={isUploading}
          onClick={() => componentFileInputsRef.current[uploadKey]?.click()}
        >
          <FiUpload /> {isUploading ? 'Uploading...' : 'Upload Voucher'}
        </button>
      </div>
    );
  };

  const statCards = [
    {
      label: 'Total Bookings',
      value: summaryCounts.total,
      action: 'View all bookings',
      className: 'is-blue',
      icon: <FiBriefcase />,
      onClick: () => {
        setQuickFilter('');
        setCurrentPage(1);
      },
    },
    {
      label: 'Payment Pending',
      value: summaryCounts.paymentPending,
      action: 'View pending payments',
      className: 'is-amber',
      icon: <FiCreditCard />,
      onClick: () => {
        setQuickFilter('');
        setPaymentFilter('pending');
      },
    },
    {
      label: 'Upcoming in 7 Days',
      value: summaryCounts.upcomingSevenDays,
      action: 'View upcoming',
      className: 'is-green',
      icon: <FiCalendar />,
      onClick: () => {
        setQuickFilter('');
        setTravelDateFilter('upcoming-7');
      },
    },
    {
      label: 'Voucher Pending',
      value: summaryCounts.voucherPending,
      action: 'View voucher pending',
      className: 'is-red',
      icon: <FiFileText />,
      onClick: () => {
        setQuickFilter('voucher-pending');
        setVoucherFilter('');
      },
    },
  ];

  return (
    <Layout>
      <div className="crm-page packages-page bookings-page">
        <div className="packages-header bookings-page__header">
          <div>
            <h2>Bookings</h2>
            <p className="page-hint">Track payment progress, booked components, vouchers, and assignment actions.</p>
          </div>
          <div className="packages-actions">
            <button className="btn" onClick={() => setShowQueryPicker(true)} title="Create booking from a query">
              <FiPlus /> Create Booking
            </button>
          </div>
        </div>

        <div className="bookings-stats-grid">
          {statCards.map((card) => (
            <button
              key={card.label}
              type="button"
              className={`bookings-stat-card ${card.className}`}
              onClick={card.onClick}
            >
              <span className="bookings-stat-card__icon">{card.icon}</span>
              <span className="bookings-stat-card__content">
                <span className="bookings-stat-card__label">{card.label}</span>
                <span className="bookings-stat-card__value">{card.value.toLocaleString('en-IN')}</span>
                <span className="bookings-stat-card__action">{card.action} <FiArrowRight /></span>
              </span>
            </button>
          ))}
        </div>

        <div className="bookings-filter-shell">
          <div className="bookings-filter-row">
            <label className="bookings-search-field">
              <FiSearch />
              <input
                type="text"
                aria-label="Search bookings"
                value={searchTerm}
                onChange={(event) => setSearchTerm(event.target.value)}
                placeholder="Search by package, customer, booking ID, hotel, flight"
              />
            </label>

            <label className="bookings-select-field">
              <span className="bookings-select-field__icon"><FiChevronDown /></span>
              <select value={statusFilter} onChange={(event) => setStatusFilter(event.target.value)} aria-label="Booking status">
                <option value="">Booking Status</option>
                {statusOptions.map((status) => (
                  <option key={status} value={status}>{status}</option>
                ))}
              </select>
            </label>

            <label className="bookings-select-field">
              <span className="bookings-select-field__icon"><FiChevronDown /></span>
              <select value={paymentFilter} onChange={(event) => setPaymentFilter(event.target.value)} aria-label="Payment status">
                <option value="">Payment Status</option>
                <option value="pending">Pending</option>
                <option value="partial">Partially Paid</option>
                <option value="overdue">Overdue</option>
                <option value="paid">Paid in Full</option>
              </select>
            </label>

            <label className="bookings-select-field">
              <span className="bookings-select-field__leading"><FiCalendar /></span>
              <span className="bookings-select-field__icon"><FiChevronDown /></span>
              <select value={travelDateFilter} onChange={(event) => setTravelDateFilter(event.target.value)} aria-label="Travel date">
                <option value="">Travel Date</option>
                <option value="upcoming">Upcoming</option>
                <option value="upcoming-7">Upcoming in 7 Days</option>
                <option value="past">Past Travel</option>
              </select>
            </label>

            <label className="bookings-select-field">
              <span className="bookings-select-field__leading"><FiUser /></span>
              <span className="bookings-select-field__icon"><FiChevronDown /></span>
              <select value={assignedFilter} onChange={(event) => setAssignedFilter(event.target.value)} aria-label="Assigned to">
                <option value="">Assigned To</option>
                <option value="assigned">Assigned</option>
                <option value="unassigned">Unassigned</option>
                {assignedUserOptions.map((user: any) => (
                  <option key={user.id} value={String(user.id)}>{user.name}</option>
                ))}
              </select>
            </label>

            <label className="bookings-select-field">
              <span className="bookings-select-field__leading"><FiSettings /></span>
              <span className="bookings-select-field__icon"><FiChevronDown /></span>
              <select value={voucherFilter} onChange={(event) => setVoucherFilter(event.target.value)} aria-label="More filters">
                <option value="">More Filters</option>
                <option value="pending">Voucher Pending</option>
                <option value="partial">Voucher Partially Uploaded</option>
                <option value="uploaded">Voucher Uploaded</option>
              </select>
            </label>

            <button type="button" className="bookings-reset-button" onClick={resetFilters}>
              <FiRefreshCw /> Reset filters
            </button>
          </div>
        </div>

        <div className="bookings-quick-filters">
          <span className="bookings-quick-filters__label">Quick Filters:</span>
          <button type="button" className={`bookings-chip ${quickFilter === 'upcoming' ? 'is-active' : ''}`} onClick={() => setQuickFilter(quickFilter === 'upcoming' ? '' : 'upcoming')}>
            <FiCalendar /> Upcoming
          </button>
          <button type="button" className={`bookings-chip is-danger ${quickFilter === 'payment-overdue' ? 'is-active' : ''}`} onClick={() => setQuickFilter(quickFilter === 'payment-overdue' ? '' : 'payment-overdue')}>
            <FiAlertCircle /> Payment Overdue
          </button>
          <button type="button" className={`bookings-chip is-amber ${quickFilter === 'voucher-pending' ? 'is-active' : ''}`} onClick={() => setQuickFilter(quickFilter === 'voucher-pending' ? '' : 'voucher-pending')}>
            <FiFileText /> Voucher Pending
          </button>
          <button type="button" className={`bookings-chip is-violet ${quickFilter === 'unassigned' ? 'is-active' : ''}`} onClick={() => setQuickFilter(quickFilter === 'unassigned' ? '' : 'unassigned')}>
            <FiUserPlus /> Unassigned
          </button>
          <button type="button" className={`bookings-chip is-green ${quickFilter === 'completed' ? 'is-active' : ''}`} onClick={() => setQuickFilter(quickFilter === 'completed' ? '' : 'completed')}>
            <FiCheckCircle /> Completed
          </button>
        </div>

        <div className="bookings-toolbar">
          <div className="bookings-toolbar__count">Showing {filteredBookings.length} booking{filteredBookings.length === 1 ? '' : 's'}</div>

          <div className="bookings-toolbar__actions">
            <div className="bookings-sort-field">
              <span>Sort by:</span>
              <label className="bookings-select-field bookings-select-field--sort">
                <span className="bookings-select-field__icon"><FiChevronDown /></span>
                <select value={sortBy} onChange={(event) => setSortBy(event.target.value)} aria-label="Sort bookings">
                  <option value="travel-newest">Travel Date (Newest)</option>
                  <option value="travel-oldest">Travel Date (Oldest)</option>
                  <option value="created-newest">Created Date (Newest)</option>
                  <option value="payment-highest">Highest Balance</option>
                  <option value="payment-lowest">Lowest Balance</option>
                </select>
              </label>
            </div>

            <div className="bookings-layout-toggle" role="group" aria-label="Layout">
              <button type="button" className={layout === 'list' ? 'is-active' : ''} onClick={() => setLayout('list')} aria-label="List view">
                <FiList />
              </button>
              <button type="button" className={layout === 'compact' ? 'is-active' : ''} onClick={() => setLayout('compact')} aria-label="Compact view">
                <FiGrid />
              </button>
            </div>
          </div>
        </div>

        <div className={`bookings-list bookings-list--${layout}`}>
          {visibleBookings.map((card) => {
            const isExpanded = expandedBookingId === card.booking.id;

            return (
              <article
                key={`booking-${String(card.booking.id)}`}
                className={`booking-card ${card.statusTone.className} ${isExpanded ? 'is-expanded' : ''}`}
                onClick={() => toggleBookingExpansion(card.booking.id)}
                onKeyDown={(event) => {
                  if (event.key === 'Enter' || event.key === ' ') {
                    event.preventDefault();
                    toggleBookingExpansion(card.booking.id);
                  }
                }}
                tabIndex={0}
                role="button"
                aria-expanded={isExpanded}
              >
                <div className="booking-card__accent" />
                <div className="booking-card__body">
                  <section className="booking-card__identity">
                    <div className="booking-card__icon">
                      <FiMapPin />
                    </div>
                    <div className="booking-card__identity-copy">
                      <div className="booking-card__title-row">
                        <h3>{card.title}</h3>
                        <span className={`booking-card__badge ${card.statusTone.className}`}>{card.statusTone.label}</span>
                      </div>

                      <div className="booking-card__meta-row">
                        <span><FiCalendar /> {card.dateRange}</span>
                        <span>{card.nights > 0 ? `${card.nights} Night${card.nights === 1 ? '' : 's'}` : 'Night plan pending'}</span>
                        <span>{card.travellerLabel}</span>
                      </div>

                      <div className="booking-card__detail-row">
                        <span>ID: {getBookingReference(card.booking)}</span>
                      </div>

                      <div className="booking-card__detail-row">
                        <span><FiUser /> Assigned to: <strong>{card.assignedName || 'Unassigned'}</strong></span>
                      </div>
                    </div>
                  </section>

                  <section className="booking-card__payment">
                    <div className="booking-card__section-title">Payment</div>

                    <div className="booking-card__payment-head">
                      <div className="booking-card__payment-copy">
                        <strong>{formatMoney(card.paidAmount)}</strong>
                        <span>of {formatMoney(card.totalAmount)}</span>
                      </div>
                      <span className={`booking-card__percentage is-${card.paymentStatus}`}>{card.paymentPercentage}%</span>
                    </div>

                    <div className="booking-card__progress">
                      <span className={`booking-card__progress-fill is-${card.paymentStatus}`} style={{ width: `${card.paymentPercentage}%` }} />
                    </div>

                    <div className="booking-card__balance">
                      Balance: <strong>{formatMoney(card.balanceAmount)}</strong>
                    </div>
                  </section>

                  <section className="booking-card__components">
                    <div className="booking-card__section-title">Components</div>
                    <div className="booking-card__component-grid">
                      <div className="booking-card__component-item is-blue">
                        <span><FiHome /></span>
                        <strong>{card.componentCounts.hotels}</strong>
                        <small>Hotels</small>
                      </div>
                      <div className="booking-card__component-item is-green">
                        <span><FiTruck /></span>
                        <strong>{card.componentCounts.cabs}</strong>
                        <small>Cab</small>
                      </div>
                      <div className="booking-card__component-item is-violet">
                        <span><FiSend /></span>
                        <strong>{card.componentCounts.flights}</strong>
                        <small>Flights</small>
                      </div>
                      <div className="booking-card__component-item is-amber">
                        <span><FiMapPin /></span>
                        <strong>{card.componentCounts.activities}</strong>
                        <small>Activities</small>
                      </div>
                    </div>

                    <div className={`booking-card__voucher booking-card__voucher--${card.voucherStatus}`}>
                      Voucher: <strong>{card.voucherStatus === 'uploaded' ? 'Uploaded' : card.voucherStatus === 'partial' ? 'Partially Uploaded' : 'Pending'}</strong>
                    </div>
                  </section>

                  <section className="booking-card__actions" onClick={stopCardToggle}>
                    <button type="button" className="btn booking-card__primary-action" onClick={() => openAddPaymentModal(card.booking)}>
                      Record Payment
                    </button>
                    <button type="button" className="btn-secondary booking-card__secondary-action" onClick={() => toggleBookingExpansion(card.booking.id)}>
                      Manage Vouchers
                    </button>
                  </section>
                </div>

                {isExpanded && (
                  <div className="booking-card__expanded" onClick={stopCardToggle}>
                    <div className="booking-card__expanded-top">
                      <div className="booking-card__expanded-links">
                        {card.booking.query_id && (
                          <button type="button" className="booking-card__link-button" onClick={() => setSelectedQueryId(card.booking.query_id!)}>
                            Open Query
                          </button>
                        )}
                        {card.booking.customer?.id ? (
                          <button type="button" className="booking-card__link-button" onClick={() => openCustomerModal(card.booking.customer?.id)}>
                            Open Customer
                          </button>
                        ) : null}
                        <button type="button" className="booking-card__link-button" onClick={() => void openPaymentHistoryModal(card.booking)}>
                          Payment History
                        </button>
                        <button type="button" className="booking-card__link-button" onClick={() => openAssignmentModal(card.booking)}>
                          Reassign Booking
                        </button>
                      </div>

                      <div className="booking-card__expanded-meta">
                        Booking #{String(card.booking.id)} • Created {formatDate(card.booking.created_at)}
                      </div>
                    </div>

                    <BookingComponentTables
                      summary={card.componentSummary}
                      compact
                      emptyText="No component details available"
                      renderVoucherActions={(row) => renderComponentVoucherActions(card.booking, row)}
                    />
                  </div>
                )}
              </article>
            );
          })}

          {visibleBookings.length === 0 && (
            <div className="booking-empty-state">
              <strong>No bookings found</strong>
              <span>Try adjusting the filters or search term.</span>
            </div>
          )}
        </div>

        {filteredBookings.length > 0 && (
          <div className="bookings-pagination">
            <div className="bookings-pagination__summary">
              Showing {(currentPage - 1) * pageSize + 1} to {Math.min(currentPage * pageSize, filteredBookings.length)} of {filteredBookings.length} bookings
            </div>

            <div className="bookings-pagination__controls">
              <button type="button" className="btn-secondary page-btn" onClick={() => setCurrentPage((page) => Math.max(1, page - 1))} disabled={currentPage === 1}>
                <FiChevronDown className="bookings-pagination__left" />
              </button>

              <div className="bookings-pagination__pages">
                {paginationItems.map((item, index) => (
                  item === 'ellipsis' ? (
                    <span key={`ellipsis-${index}`} className="bookings-pagination__ellipsis">...</span>
                  ) : (
                    <button
                      key={`page-${item}`}
                      type="button"
                      className={`btn-secondary page-btn ${item === currentPage ? 'active' : ''}`}
                      onClick={() => setCurrentPage(item)}
                    >
                      {item}
                    </button>
                  )
                ))}
              </div>

              <button type="button" className="btn-secondary page-btn" onClick={() => setCurrentPage((page) => Math.min(totalPages, page + 1))} disabled={currentPage === totalPages}>
                <FiChevronDown />
              </button>
            </div>

            <label className="bookings-pagination__page-size">
              <select value={pageSize} onChange={(event) => setPageSize(Number(event.target.value))} aria-label="Bookings per page">
                <option value={10}>10 per page</option>
                <option value={25}>25 per page</option>
                <option value={50}>50 per page</option>
              </select>
            </label>
          </div>
        )}
      </div>

      {selectedCustomer && (
        <CustomerInfoModal
          customer={selectedCustomer}
          onClose={() => setSelectedCustomer(null)}
        />
      )}

      {selectedQueryId && (
        <QueryInfoModal
          queryId={selectedQueryId}
          onClose={() => setSelectedQueryId(null)}
        />
      )}

      {showAssignmentModal && selectedBooking && (
        <AssignmentModal
          bookingId={selectedBooking.id}
          users={allUsers}
          onClose={() => setShowAssignmentModal(false)}
          onAssign={handleAssign}
        />
      )}

      {showPaymentHistory && (selectedBookingWithHistory || selectedBooking) && (
        <PaymentHistoryModal
          isOpen={true}
          onClose={() => {
            setSelectedBooking(null);
            setSelectedBookingWithHistory(null);
            setShowPaymentHistory(false);
          }}
          payments={(selectedBookingWithHistory || selectedBooking)?.payments || []}
          total={(selectedBookingWithHistory || selectedBooking)?.total_price || 0}
          loading={loadingHistory}
        />
      )}

      {showAddPaymentModal && selectedBooking && (
        <AddPaymentModal
          isOpen={true}
          onClose={() => {
            setSelectedBooking(null);
            setShowAddPaymentModal(false);
          }}
          onSubmit={handleAddPayment}
          defaultAmount={selectedBooking.total_price}
          totalPrice={selectedBooking.total_price || 0}
          paidAmount={getPaidAmount(selectedBooking)}
        />
      )}

      {showQueryPicker && (
        <QueryPickerModal
          onClose={() => setShowQueryPicker(false)}
          onSelect={(query) => {
            setSelectedQueryForBooking(query);
            setShowQueryPicker(false);
            setShowCreateBookingModal(true);
          }}
        />
      )}

      {showCreateBookingModal && selectedQueryForBooking && (
        <AddBookingModal
          query={selectedQueryForBooking}
          onClose={() => {
            setShowCreateBookingModal(false);
            setSelectedQueryForBooking(null);
          }}
          onCreated={() => handleBookingCreated(selectedQueryForBooking)}
        />
      )}
    </Layout>
  );
};

export default BookingsPage;
